import { useEffect, useMemo, useState } from 'preact/hooks';
import { useMe, signOut } from '../../auth/useMe';
import { fetchProviders } from '../../auth/providers';
import type { Connection, ProviderInfo } from '../../auth/types';
import { Eyebrow } from '../../primitives';
import { AccountShell } from '../../primitives/account';
import { useToast } from '../../primitives/admin';
import { useTranslation } from '../../i18n';

/**
 * Sign-in section of /me/account. Surfaces the user's connected OAuth
 * providers and lets them connect / disconnect each one.
 *
 * <ul>
 *   <li>Connect - hits the provider's <c>challengeUrl</c> with a
 *     <c>?return=/me/account/sign-in</c> so the AccountLink contributor
 *     takes the AttachedToCurrent path and the user lands back here
 *     with the new binding listed.</li>
 *   <li>Disconnect - typed-confirm against the provider id, then DELETE
 *     <c>/api/me/account/connections/{provider}/{keyHash}</c>. The server
 *     guards against removing the last binding (409 last-provider).</li>
 * </ul>
 */
export function AccountSignInPage() {
    const t = useTranslation();
    const me = useMe();
    const toast = useToast();
    const [providers, setProviders] = useState<ProviderInfo[] | null>(null);
    const [providersError, setProvidersError] = useState<string | null>(null);
    const [busyKey, setBusyKey] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        fetchProviders()
            .then((list) => {
                if (cancelled) return;
                setProviders(list);
            })
            .catch((err: unknown) => {
                if (cancelled) return;
                setProvidersError(err instanceof Error ? err.message : String(err));
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const connections = useMemo<Connection[]>(
        () =>
            me.status === 'authenticated' && me.user?.connections
                ? me.user.connections
                : [],
        [me],
    );

    const linkedProviderIds = useMemo(
        () => new Set(connections.map((c) => c.provider.toLowerCase())),
        [connections],
    );

    const unlinkedProviders = useMemo<ProviderInfo[]>(
        () =>
            (providers ?? []).filter(
                (p) => !linkedProviderIds.has(p.id.toLowerCase()),
            ),
        [providers, linkedProviderIds],
    );

    const disconnect = async (c: Connection) => {
        if (busyKey) return;
        if (connections.length <= 1) {
            toast.push(
                'error',
                t('me.signin.linked.lastProviderError'),
            );
            return;
        }
        const ok = window.confirm(
            t('me.signin.linked.disconnectConfirm', { provider: c.provider }),
        );
        if (!ok) return;
        const key = `${c.provider}:${c.keyHash}`;
        setBusyKey(key);
        try {
            const res = await fetch(
                `/api/me/account/connections/${encodeURIComponent(c.provider)}/${encodeURIComponent(c.keyHash)}`,
                { method: 'DELETE', credentials: 'include' },
            );
            if (res.ok || res.status === 204) {
                toast.push('success', t('me.signin.linked.disconnected', { provider: c.provider }));
                setTimeout(() => window.location.reload(), 700);
                return;
            }
            const body = await res.json().catch(() => ({}));
            const message =
                (body as { message?: string; error?: string }).message ??
                (body as { error?: string }).error ??
                `HTTP ${res.status}`;
            toast.push('error', message);
        } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            toast.push('error', t('me.signin.linked.disconnectError', { msg }));
        } finally {
            setBusyKey(null);
        }
    };

    const connect = (p: ProviderInfo) => {
        const sep = p.challengeUrl.indexOf('?') === -1 ? '?' : '&';
        const returnTo = '/me/account/sign-in';
        const href = `${p.challengeUrl}${sep}return=${encodeURIComponent(returnTo)}`;
        window.location.assign(href);
    };

    return (
        <AccountShell
            title={t('me.signin.title')}
            subtitle={t('me.signin.subtitle')}
        >
            <div class="mx-auto w-full max-w-2xl space-y-8 px-[clamp(20px,3.5vw,56px)] py-10">
                <section>
                    <Eyebrow class="mb-3">{t('me.signin.linked.eyebrow')}</Eyebrow>
                    {me.status === 'loading' && (
                        <p class="text-sm text-fg-muted">{t('me.signin.loading')}</p>
                    )}
                    {me.status === 'authenticated' && (
                        <LinkedList
                            connections={connections}
                            busyKey={busyKey}
                            onDisconnect={(c) => void disconnect(c)}
                            canRemoveAny={connections.length > 1}
                        />
                    )}
                </section>

                <section>
                    <Eyebrow class="mb-3">{t('me.signin.connect.eyebrow')}</Eyebrow>
                    <p class="mb-3 text-sm text-fg-muted">
                        {t('me.signin.connect.body')}
                    </p>
                    {providersError && (
                        <p class="mb-3 text-sm text-state-failed">
                            {t('me.signin.connect.loadError', { error: providersError })}
                        </p>
                    )}
                    {providers === null && !providersError && (
                        <p class="text-sm text-fg-muted">{t('me.signin.loading')}</p>
                    )}
                    {providers !== null && unlinkedProviders.length === 0 && (
                        <p class="text-sm text-fg-muted">
                            {t('me.signin.connect.allLinked')}
                        </p>
                    )}
                    {unlinkedProviders.length > 0 && (
                        <div class="flex flex-wrap gap-2">
                            {unlinkedProviders.map((p) => (
                                <button
                                    key={p.id}
                                    type="button"
                                    onClick={() => connect(p)}
                                    class="rounded border border-rule-strong bg-surface-raised px-4 py-2 text-sm text-fg-primary transition-colors hover:border-accent hover:text-accent"
                                >
                                    {t('me.signin.connect.connectCta', { name: p.name || p.id })}
                                </button>
                            ))}
                        </div>
                    )}
                </section>

                <section>
                    <Eyebrow class="mb-3">{t('me.signin.signout.eyebrow')}</Eyebrow>
                    <p class="mb-3 text-sm text-fg-muted">
                        {t('me.signin.signout.body')}
                    </p>
                    <button
                        type="button"
                        onClick={() => signOut('/')}
                        class="rounded border border-rule-strong px-4 py-2 text-sm text-fg-primary transition-colors hover:border-state-failed hover:text-state-failed"
                    >
                        {t('me.signin.signout.cta')}
                    </button>
                </section>
            </div>
        </AccountShell>
    );
}

function LinkedList({
    connections,
    busyKey,
    onDisconnect,
    canRemoveAny,
}: {
    connections: Connection[];
    busyKey: string | null;
    onDisconnect: (c: Connection) => void;
    canRemoveAny: boolean;
}) {
    const t = useTranslation();
    if (connections.length === 0) {
        return (
            <p class="text-sm text-fg-muted">
                {t('me.signin.linked.empty')}
            </p>
        );
    }
    return (
        <ul class="space-y-2">
            {connections.map((c) => {
                const key = `${c.provider}:${c.keyHash}`;
                const busy = busyKey === key;
                return (
                    <li
                        key={key}
                        class="flex items-center justify-between gap-3 rounded border border-rule bg-surface-raised px-3 py-2"
                    >
                        <div class="min-w-0">
                            <div class="font-mono text-[12px] uppercase tracking-[0.14em] text-fg-muted">
                                {c.provider}
                            </div>
                            {c.displayName && (
                                <div class="truncate text-sm text-fg-primary">
                                    {c.displayName}
                                </div>
                            )}
                        </div>
                        <div class="flex items-center gap-3">
                            <span class="font-mono text-[10px] uppercase tracking-[0.14em] text-state-ready">
                                {t('me.signin.linked.linkedBadge')}
                            </span>
                            <button
                                type="button"
                                onClick={() => onDisconnect(c)}
                                disabled={busy || !canRemoveAny}
                                title={
                                    canRemoveAny
                                        ? t('me.signin.linked.disconnectTitle', { provider: c.provider })
                                        : t('me.signin.linked.connectFirstHint')
                                }
                                class="rounded border border-rule-strong px-2.5 py-1 text-[12px] text-fg-muted transition-colors hover:border-state-failed hover:text-state-failed disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                {busy ? '…' : t('me.signin.linked.disconnectCta')}
                            </button>
                        </div>
                    </li>
                );
            })}
        </ul>
    );
}
